import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ImpersonationService } from '../../core/services/impersonation.service';
import { ExtranetUserService } from '../../core/services/extranet-user.service';
import { UserService } from '../../core/services/user.service';
import { CookiesService } from '../../core/services/cookies.service';
import { EventService } from '../../core/services/event.service';
import { ConfirmationService } from '../../core/services/confirmation.service';
import { TranslateService } from '@ngx-translate/core';

/**
 * Banner component shown when a Super Admin is impersonating another user.
 * Displays the impersonated user's name and provides an exit button.
 */
@Component({
  selector: 'app-impersonation-banner',
  templateUrl: './impersonation-banner.component.html',
  styleUrls: ['./impersonation-banner.component.scss'],
  standalone: false
})
export class ImpersonationBannerComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isImpersonating = false;
  targetUserName = '';
  adminUserName = '';
  isExiting = false;

  constructor(
    private impersonationService: ImpersonationService,
    private extranetUserService: ExtranetUserService,
    private userService: UserService,
    private cookiesService: CookiesService,
    private eventService: EventService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Subscribe to impersonation state changes
    this.impersonationService.impersonationState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.isImpersonating = state !== null;
        if (state) {
          this.targetUserName = state.targetUserName;
          this.adminUserName = state.adminUserName;
        }
        // Add/remove body class to adjust layout
        this.updateBodyClass();
      });
  }

  /**
   * Add or remove the 'impersonating' class on the body element
   * to push down the layout when the banner is visible.
   */
  private updateBodyClass(): void {
    if (this.isImpersonating) {
      document.body.classList.add('impersonating');
    } else {
      document.body.classList.remove('impersonating');
    }
  }

  ngOnDestroy(): void {
    // Clean up body class
    document.body.classList.remove('impersonating');
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Exit impersonation and return to original admin session.
   */
  exitImpersonation(): void {
    const state = this.impersonationService.getState();
    if (!state) {
      return;
    }

    this.isExiting = true;
    this.eventService.broadcast('globalLoader:show', null);

    this.extranetUserService.exitImpersonation(state.adminUserId, state.targetUserId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.data?.token) {
            // Clear impersonation state
            this.impersonationService.endImpersonation();

            // Clear stale impersonated user data
            this.cookiesService.delete('user');
            localStorage.removeItem('lastHotelId');

            // Set admin token and fetch fresh admin user data from /api/v2/users/me
            // before redirecting, so the admin's name and permissions are available immediately
            this.cookiesService.update('token', response.data.token);
            this.userService.resolveUser().subscribe({
              next: () => {
                window.location.href = '/dashboard';
              },
              error: () => {
                // Redirect even if resolve fails — UserService constructor will retry on reload
                window.location.href = '/dashboard';
              }
            });
          } else {
            this.confirmationService.swalError(this.translate.instant('notifications.impersonation.exit_no_token'));
            this.isExiting = false;
            this.eventService.broadcast('globalLoader:hide', null);
          }
        },
        error: (error: { error?: { message?: string } }) => {
          this.confirmationService.swalError(error.error?.message || this.translate.instant('notifications.impersonation.exit_failed'));
          this.isExiting = false;
          this.eventService.broadcast('globalLoader:hide', null);
        }
      });
  }
}
